import type { Dispatch } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import type { AppState } from '@/store/store.ts';
import { selectSwapPrices } from '@/store/swap/selectors/rfs.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import type { SwapRfsPrice } from '@/store/swap/state.ts';
import { convertRateToPoints } from '@/utils/number-point.ts';
import { roundToFixed } from '@/utils/number.ts';

export const copyToClipBoardThunk = (tileId: string) => async (_dispatch: Dispatch, getState: () => AppState) => {
  const state = getState();
  const tile = selectSwapTile(state, tileId);
  const prices = selectSwapPrices(state, tileId);

  if (isNotDefined(tile) || isNotDefined(prices)) {
    return;
  }

  const currencyPairPrecision = selectCurrencyPairPrecision(state, tile.properties.currencyPair) ?? 0;

  const {
    properties: {
      nearNotional = '?',
      farNotional = '?',
      nearExpiryDate = '?',
      nearExpiryDateTenor = '?',
      farExpiryDate = '?',
      farExpiryDateTenor = '?',
      rfs = {} as SwapRfsPrice,
    },
  } = tile;

  const { commercialMarginAmountCurrency, nearPrice, nearPoints, farPrice, farPoints, rates } = prices;

  const midPoints =
    rfs.status === 'STREAMED' && isDefined(rfs.quote.delta)
      ? convertRateToPoints(rfs.quote.delta, currencyPairPrecision - 1)
      : undefined;

  const [way0, way1] = prices.commercialMarginAmountCurrencyIndex === 1 ? ['Buy', 'Sell'] : ['Sell', 'Buy'];

  const formatPointsValue = (points: number) => `${points > 0 ? '+' : ''}${roundToFixed(points, 2)}`;
  const formatRate = (rate: number) => roundToFixed(rate, currencyPairPrecision);

  const summary = `Swap ${tile.properties.currencyPair ?? '?/?'}
Spot ref: ${prices.spot.bid}/${prices.spot.ask}, Dodd Frank mid pts: ${midPoints}
-------${way0} & ${way1} ${commercialMarginAmountCurrency} (BID)-------
Near leg: ${way0} ${nearNotional} ${commercialMarginAmountCurrency} @${formatRate(nearPrice.bid)} (${formatPointsValue(nearPoints.bid)}), value date ${nearExpiryDate} (${nearExpiryDateTenor})
Far leg: ${way1} ${farNotional} ${commercialMarginAmountCurrency} @${formatRate(farPrice.bid)} (${formatPointsValue(farPoints.bid)}), value date ${farExpiryDate} (${farExpiryDateTenor})
ALL-IN points : (${formatPointsValue(roundToFixed(rates.bid, 3))})
-------${way1} & ${way0} ${commercialMarginAmountCurrency} (OFFER)-------
Near leg: ${way1} ${nearNotional} ${commercialMarginAmountCurrency} @${formatRate(nearPrice.ask)} (${formatPointsValue(nearPoints.ask)}), value date ${nearExpiryDate} (${nearExpiryDateTenor})
Far leg: ${way0} ${farNotional} ${commercialMarginAmountCurrency} @${formatRate(farPrice.ask)} (${formatPointsValue(farPoints.ask)}), value date ${farExpiryDate} (${farExpiryDateTenor})
ALL-IN points : (${formatPointsValue(roundToFixed(rates.ask, 3))})`;

  await navigator.clipboard.writeText(summary);
};
